import React, { useEffect, useState, useRef } from "react";
import { Hand, X } from "lucide-react";
import { rtdb } from "utils/firebase"; // Import the rtdb from your firebase config file
import { environment } from "../utils/constant";
import { useNavigate } from "react-router-dom";
const NotificationDropdown = ({ isOpen, onClose, notifications }) => {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  // Helper function to format dates
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      };
      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      return dateString;
    }
  };

  // Mark all unread notifications as read when dropdown opens
  useEffect(() => {
    if (isOpen && notifications.length > 0) {
      const organizationId = notifications[0].organization;
      const notificationsRef = rtdb.ref(`notifications-${environment}`);
      // console.log(
      //   "Marking notifications as read for organization:",
      //   notifications
      // );
      // Get all notifications for the current organization
      notifications
        .filter((notification) => !notification.read)
        .forEach((notification) => {
          // Update each unread notification to read
          notificationsRef
            .orderByChild("organization")
            .equalTo(organizationId)
            .once("value", (snapshot) => {
              const notificationsData = snapshot.val() || {};
              Object.keys(notificationsData).forEach((key) => {
                if (
                  notificationsData[key].title === notification.title &&
                  notificationsData[key].date === notification.date &&
                  notificationsData[key].message === notification.message &&
                  !notificationsData[key].read
                ) {
                  notificationsRef.child(key).update({ read: true });
                }
              });
            });
        });
    }
  }, [isOpen, notifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleNotificationClick = (notification) => {
    const materialsSection = [
      "Material Created",
      "Vendor Created",
      "Delivery Created",
      "Part Payment Created",
      "Delivery Marked as Delivered",
      "Stock Created",
      "Stock Update",
    ];
    const financeSection = [
      "Transaction Created",
      "Worker Transaction Created",
    ];
    const workersSection = ["New Worker Created"];
    const tasksSection = [
      "New Task Group Created",
      "New Task Created",
      "Task Updated",
      "New SubTask Created",
      "SubTask Updated",
      "New SubTask Update",
      "SubTask Update Needs Approval",
      "Task Member Added",
      "Task Member Removed",
      "Task Approver Assigned",
      "Task Approver Removed",
    ];

    if (!notification.site) {
      console.error(
        "Notification does not have a site associated with it:",
        notification
      );
      return;
    }

    // Navigate to Snags for chat notifications
    if (notification.type === "snag_message") {
      const query = notification.snagId
        ? `?snagId=${encodeURIComponent(notification.snagId)}`
        : "";
      navigate(`/site/${notification.site}/snags${query}`);
      return;
    }

    if (materialsSection.includes(notification.title)) {
      navigate(`/site/${notification.site}/materials`);
    } else if (financeSection.includes(notification.title)) {
      navigate(`/site/${notification.site}/finance`);
    } else if (workersSection.includes(notification.title)) {
      navigate(`/site/${notification.site}/workers`);
    } else if (tasksSection.includes(notification.title)) {
      navigate(`/site/${notification.site}/tasks`);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-80 max-h-96 bg-white rounded-lg shadow-lg z-50 overflow-hidden"
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-800">Notifications</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="overflow-y-auto max-h-80">
        {notifications && notifications.length > 0 ? (
          <div>
            {notifications.map((notification, index) => (
              <div
                key={index}
                className={`p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  !notification.read ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium text-[#3B0069] text-sm">
                    {notification.title}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {formatDate(notification.date)}
                  </span>
                </div>
                <p className="text-xs text-gray-700">{notification.message}</p>
                <button
                  className="mt-2 text-xs text-[#3B0069] hover:text-[#58267F] font-medium"
                  onClick={() => {
                    // Navigation logic to be handled by parent component
                    handleNotificationClick(notification);
                  }}
                  style={{
                    display: notification.site ? "block" : "none",
                  }}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p className="text-sm">No notifications available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;